import type TTQuery from '../cms/client/TTQuery';
import type { CmsContentContract } from '../cms/content/ko/CmsContentContract';
import type TTFiltering from '../cms/client/TTFiltering';
import axios, { type AxiosInstance } from 'axios';
import { encode } from 'qss';
import type { ICMSRuntimeClient } from '../cms/client/ICMSRuntimeClient';
import type { PaginationInfo } from '../cms/client/PaginationInfo';

interface CareersEvent {
    id: number;
    content_type_ref: string;
    cover_media: {
        url: string
    };
    address: string;
    externalId: string;
    location: string;
    title: string;
    permalink?: string;
    url?: string;
    startDate: string;
    endDate?: string;
}

interface Paginated<T> {
    total: {
        value: number;
        relation: 'eq' | 'gte';
    };
    items: T[];
}

interface Params {
    country?: string[];
    first?: number;
    size?: number;
    fromStartDate?: string;
}

export interface TTCareersQuery extends TTQuery {
    fromStartDate?: boolean;
}

function transformParams(query: TTCareersQuery): Params {
    const filtering = query.filtering as TTFiltering[];
    const country = filtering.find(f => f.columnField === 'country');
    const params: Params = {};
    if (country) {
        params.country = country.value as unknown as string[]; // TODO
    }
    if (query.pageSize >= 0) {
        params.size = +query.pageSize;
    }
    if (query.firstItem >= 0) {
        params.first = +query.firstItem;
    }
    if (query.fromStartDate) {
        params.fromStartDate = new Date().toISOString().slice(0, 10);
    }
    return params;
}

function transformItem(ce: CareersEvent): CmsContentContract {
    return {
        id: ce.id,
        externalId: ce.externalId,
        type: 'careers_event',
        title: ce.title,
        permalink: ce.permalink,
        url: ce.url,
        eventDate: ce.startDate,
        closingDate: ce.endDate,
        publicationDate: undefined,
        // TODO eventTimezone: ce.eventTimezone
        meta: [
            {
                key: 'cover_media',
                value: JSON.stringify(ce.cover_media),
                optionValues: []
            },
            {
                key: 'event_location',
                value: ce.location,
                optionValues: []
            },
            {
                key: 'card_type',
                optionValues: [
                    {
                        optionValue: {
                            referenceId: 'mk_card_type_careersevent',
                        }
                    }
                ]
            },
            {
                key: 'content_type',
                optionValues: [
                    {
                        optionValue: {
                            referenceId: ce.url ? 'mk_content_type_externalpage' : 'mk_content_type_overlay',
                        }
                    }
                ]
            }
        ],
        tags: []
    };
}

export class CareersEventApiClient implements ICMSRuntimeClient<CmsContentContract> {

    private constructor(private readonly instance: AxiosInstance) {}

    public static build(config: { endpoint: string }): CareersEventApiClient {
        const instance = axios.create({
            baseURL: config.endpoint,
            paramsSerializer: (params) => encode(params),
        });
        return new CareersEventApiClient(instance);
    }

    public async getContent<T = CmsContentContract>(contentId: number, params?: object, config?: object): Promise<{ data: T }> {
        const { data }: { data: CareersEvent } = await this.instance.get<CareersEvent>(`/careersevent2/${contentId}`, {
            params,
            ...config
        });
        return {
            data: transformItem(data) as T
        };
    }

    public async listContents<T = CmsContentContract>(query: TTCareersQuery): Promise<{ data: { list: T[], paginationInfo: PaginationInfo } }> {
        const { data }: { data: Paginated<CareersEvent> } = await this.instance.get<Paginated<CareersEvent>>('/careersevent2', {
            params: transformParams(query)
        });
        const total = data.total.value;
        let nextOffset = query.firstItem + query.pageSize;
        if (nextOffset >= total) {
            nextOffset = 0;
        }
        return {
            data: {
                list: data.items.map(transformItem) as T[],
                paginationInfo: {
                    limit: query.pageSize,
                    nextOffset,
                    startOffset: query.firstItem,
                    total
                }
            }
        };
    }
}
